// Aliases TanStack Query hooks — 消费 /api/v1/aliases 接口。
// DTO 契约走 packages/shared/dto（F10：前后端共用）。
// staleTime 60s / refetch off（CLAUDE.md §四：别名 60s/off）。

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api } from './api.js';

import type {
  AliasDTO,
  AliasListResult,
  AliasSource,
  AliasStatus,
} from '@aliashub/shared/dto';

export type { AliasDTO, AliasListResult, AliasSource, AliasStatus } from '@aliashub/shared/dto';

export interface AliasesFilter {
  domainId?: number | undefined;
  /** true = 只看 gmail_plus (domainId IS NULL) */
  domainNull?: boolean | undefined;
  source?: AliasSource | undefined;
  status?: AliasStatus | undefined;
  search?: string | undefined;
  /** 只看带这个 labelId 的别名 */
  labelId?: number | undefined;
}

export interface CreateAliasBody {
  source: AliasSource;
  domainId?: number | null;
  localPart: string;
  displayName?: string | null;
  note?: string | null;
  isTemporary?: boolean;
  expiresAt?: number | null;
}

export interface UpdateAliasBody {
  displayName?: string | null;
  note?: string | null;
  status?: AliasStatus;
  isTemporary?: boolean;
  expiresAt?: number | null;
}

const PAGE_SIZE = 30;

function buildListUrl(filter: AliasesFilter, cursor?: string): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (filter.domainId != null) params.set('domainId', String(filter.domainId));
  if (filter.domainNull) params.set('domainNull', '1');
  if (filter.source) params.set('source', filter.source);
  if (filter.status) params.set('status', filter.status);
  if (filter.search && filter.search.trim().length > 0)
    params.set('search', filter.search.trim());
  if (filter.labelId != null) params.set('labelId', String(filter.labelId));
  if (cursor) params.set('cursor', cursor);
  return `/api/v1/aliases?${params.toString()}`;
}

// ---- queries ----

export function useAliases(filter: AliasesFilter) {
  return useInfiniteQuery<
    AliasListResult,
    Error,
    InfiniteData<AliasListResult, string | undefined>,
    readonly ['aliases', 'list', AliasesFilter],
    string | undefined
  >({
    queryKey: ['aliases', 'list', filter] as const,
    queryFn: ({ pageParam }) => api.get<AliasListResult>(buildListUrl(filter, pageParam)),
    initialPageParam: undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
  });
}

export function useAlias(id: number) {
  return useQuery<AliasDTO>({
    queryKey: ['aliases', 'detail', id] as const,
    queryFn: () => api.get<AliasDTO>(`/api/v1/aliases/${id}`),
    staleTime: 60_000,
    enabled: Number.isFinite(id) && id > 0,
  });
}

// ---- mutations ----

function invalidateLists(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['aliases'] });
}

export function useCreateAlias() {
  const qc = useQueryClient();
  return useMutation<AliasDTO, Error, CreateAliasBody>({
    mutationFn: (body) => api.post<AliasDTO>('/api/v1/aliases', body),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}

export function useUpdateAlias() {
  const qc = useQueryClient();
  return useMutation<AliasDTO, Error, { id: number; patch: UpdateAliasBody }>({
    mutationFn: ({ id, patch }) => api.patch<AliasDTO>(`/api/v1/aliases/${id}`, patch),
    onSuccess: (data) => {
      qc.setQueryData(['aliases', 'detail', data.id], data);
      invalidateLists(qc);
    },
  });
}

export function useArchiveAlias() {
  const qc = useQueryClient();
  return useMutation<AliasDTO, Error, number>({
    mutationFn: (id) => api.delete<AliasDTO>(`/api/v1/aliases/${id}`),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}

export function usePauseAlias() {
  const qc = useQueryClient();
  return useMutation<AliasDTO, Error, number>({
    mutationFn: (id) => api.post<AliasDTO>(`/api/v1/aliases/${id}/pause`),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}

export function useResumeAlias() {
  const qc = useQueryClient();
  return useMutation<AliasDTO, Error, number>({
    mutationFn: (id) => api.post<AliasDTO>(`/api/v1/aliases/${id}/resume`),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}
